/* eslint-disable react/no-unstable-nested-components */
import { Button, DataTable, FlexLayout, Icon } from '@deity-io/portal-ui';
import { BaseHelmet, ConfirmationModal, CreateEditWebServiceModal, EndpointTypeBadge } from 'components';
import { APOLLO_BASE_OPTIONS, ENTITY_PATH, ENTITY_PATH_ID, toLocalDateString } from 'contracts/constants';
import { DEFAULT_PAGINATION_SIZE } from 'contracts/constants/page-title';
import { EntityWrapper } from 'layouts';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  GetWebServicesByPackageVersionDocument,
  useDeleteWebServiceMutation,
  useGetWebServicesByPackageVersionLazyQuery
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';
import { v4 } from 'uuid';

interface WebServicesProps {
  isEditable: boolean;
}
const WebServices: FC<WebServicesProps> = ({ isEditable }) => {
  const history = useHistory();
  const [paging, setPaging] = useState({ page: 1, pageSize: DEFAULT_PAGINATION_SIZE });
  const { url } = useRouteMatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [webServiceToDelete, setWebServiceToDelete] = useState<any>();
  const { pathname } = useLocation();
  const [getAllWebServices, { loading, data: webServices, fetchMore }] =
    useGetWebServicesByPackageVersionLazyQuery(APOLLO_BASE_OPTIONS);
  const [newWebServiceKey, setNewWebServiceKey] = useState(v4());
  const params: any = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const totalItems = webServices?.getWebServicesByPackageVersion?.totalItems;
  const data = webServices?.getWebServicesByPackageVersion?.data;

  const [deleteWebService] = useDeleteWebServiceMutation({
    refetchQueries: [
      {
        query: GetWebServicesByPackageVersionDocument,
        variables: {
          packageVersionUuid: params?.versionUuid,
          offset: 0,
          limit: webServices?.getWebServicesByPackageVersion?.data.length || paging.pageSize
        }
      }
    ]
  });

  useEffect(() => {
    if (params?.versionUuid) {
      getAllWebServices({
        variables: {
          packageVersionUuid: params.versionUuid,
          offset: (paging.page - 1) * paging.pageSize,
          limit: paging.pageSize
        }
      });
    }
  }, [pathname, paging]);

  const NewWebServiceButton = () => (
    <Button
      variant="outlined"
      disabled={isModalOpen}
      onClick={() => setIsModalOpen(true)}
      startIcon={<Icon icon="add" />}
    >
      New WebService
    </Button>
  );

  return (
    <>
      <BaseHelmet page="WebServices" />

      <EntityWrapper title="WebServices" button={<NewWebServiceButton />}>
        <DataTable
          data={data || []}
          isLoading={loading}
          noResultsMsg="No web services available for this version"
          columns={[
            {
              key: 'name',
              name: 'SERVICE',
              formatter: ({ value }) => <b>{value}</b>,
              width: 40
            },
            {
              key: 'schema',
              name: 'TYPE',
              align: 'left',
              formatter: props => <EndpointTypeBadge schema={props.value} />
            },
            {
              key: 'schema',
              name: 'host',
              align: 'left',
              formatter: ({ value }) => <b>{value.host}</b>
            },
            {
              key: 'modifiedAt',
              name: 'LAST MODIFIED',
              formatter: ({ value }) => toLocalDateString(value)
            },
            {
              key: 'modifiedBy',
              name: 'LAST MODIFIED BY',
              formatter: () => <div>John Smith</div>
            },
            {
              key: 'uuid',
              name: 'ACTIONS',
              align: 'left',
              isActionCell: true,
              formatter: ({ value }) => (
                <FlexLayout>
                  <Button endIcon={<Icon icon="visibility" />} onClick={() => history.push(`${url}/${value}`)} />

                  {isEditable && (
                    <Button
                      endIcon={<Icon icon="delete" color="error" />}
                      onClick={() => {
                        setWebServiceToDelete(value);
                        setIsDeleteModalOpen(true);
                      }}
                    />
                  )}
                </FlexLayout>
              )
            }
          ]}
          pageSize={paging.pageSize}
          onPageSizeChange={size => {
            setPaging({ ...paging, pageSize: size });
          }}
          totalResults={totalItems || 0}
          page={paging.page}
          showPagination
          onPageChange={pageNr => {
            setPaging({ ...paging, page: pageNr });
          }}
          onRowClick={webService => history.push(`${url}/${webService.uuid}`)}
          fullClickable
        />
      </EntityWrapper>

      <CreateEditWebServiceModal
        key={newWebServiceKey}
        isOpen={isModalOpen}
        versionUuid={params?.versionUuid}
        setOpenState={state => setIsModalOpen(state)}
        onPackageCreated={() => {
          setIsModalOpen(false);
          setNewWebServiceKey(v4());
          setPaging({ ...paging, page: 1 });
        }}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        entityType="web service"
        entityUuid={webServiceToDelete}
        onConfirm={uuid =>
          deleteWebService({
            variables: { packageVersionUuid: params.versionUuid, uuid: uuid! }
          })
        }
      />
    </>
  );
};

export default WebServices;
